import React from 'react';
import { Card } from "@/components/ui/card";
import { CheckCircle2, AlertCircle, TrendingUp, Flame, Trophy, Medal, Target, Lightbulb, Star, Zap } from "lucide-react";

// Parse markdown content into structured sections
const parseAIResponse = (content) => {
  if (!content) return [];
  
  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentItems = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines but preserve them in content
    if (!line) {
      if (currentSection && currentItems.length > 0) {
        currentSection.content = currentItems.join('\n');
        sections.push(currentSection);
        currentSection = null;
        currentItems = [];
      }
      continue;
    }
    
    // Main section headers (## or # followed by number and text)
    const sectionMatch = line.match(/^#{1,2}\s*(\d+[\.\)]\s*)?(.+)$/);
    if (sectionMatch) {
      if (currentSection) {
        currentSection.content = currentItems.join('\n');
        sections.push(currentSection);
        currentItems = [];
      }
      currentSection = {
        type: 'section',
        number: sectionMatch[1]?.trim(),
        title: sectionMatch[2].replace(/\*\*/g, '').trim(),
        content: '',
        items: []
      };
      continue;
    }
    
    // Numbered items like **#1 - "Title"** or **1. Title**
    const numberedMatch = line.match(/^\*\*#?(\d+)[\.\)\-]\s*(.+?)\*\*$/);
    if (numberedMatch) {
      if (currentSection) {
        currentSection.items.push({
          number: parseInt(numberedMatch[1]),
          title: numberedMatch[2].replace(/["""]/g, '').trim(),
          details: []
        });
      }
      continue;
    }
    
    // Sub-items with labels like - **Label:** Content
    const labeledMatch = line.match(/^-\s*\*\*(.+?)[:：]\*\*\s*(.+)$/);
    if (labeledMatch && currentSection && currentSection.items.length > 0) {
      const lastItem = currentSection.items[currentSection.items.length - 1];
      lastItem.details.push({
        label: labeledMatch[1].trim(),
        value: labeledMatch[2].trim()
      });
      continue;
    }
    
    // Regular bullet points
    const bulletMatch = line.match(/^[-•]\s*(.+)$/);
    if (bulletMatch) {
      currentItems.push(bulletMatch[1]);
      continue;
    }
    
    // Score pattern like "8.5/10" or "SCORE: 8/10"
    const scoreMatch = line.match(/(\d+(?:\.\d+)?)\s*\/\s*10/i);
    if (scoreMatch && currentSection) {
      currentSection.score = parseFloat(scoreMatch[1]);
    }
    
    // Regular content
    currentItems.push(line);
  }
  
  // Push last section
  if (currentSection) {
    currentSection.content = currentItems.join('\n');
    sections.push(currentSection);
  } else if (currentItems.length > 0) {
    sections.push({
      type: 'text',
      content: currentItems.join('\n')
    });
  }
  
  return sections;
};

// Parse inline markdown (bold, italic)
const parseInlineMarkdown = (text) => {
  if (!text) return '';
  
  // Remove markdown symbols and create styled spans
  const parts = [];
  let remaining = text;
  let key = 0;
  
  while (remaining.length > 0) {
    // Bold text **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const before = remaining.slice(0, boldMatch.index);
      if (before) parts.push(<span key={key++}>{before}</span>);
      parts.push(<strong key={key++} className="text-white font-semibold">{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }
    
    // No more matches
    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }
  
  return parts.length > 0 ? parts : text;
};

// Get severity color
const getSeverityColor = (level) => {
  const normalized = level?.toLowerCase() || '';
  if (normalized.includes('critical') || normalized.includes('high') || normalized.includes('urgent')) {
    return { dot: 'bg-red-500', text: 'text-red-400', label: 'HIGH' };
  }
  if (normalized.includes('medium') || normalized.includes('moderate')) {
    return { dot: 'bg-yellow-500', text: 'text-yellow-400', label: 'MEDIUM' };
  }
  return { dot: 'bg-green-500', text: 'text-green-400', label: 'LOW' };
};

// Problem Card Component
const ProblemCard = ({ number, title, details }) => {
  const urgencyDetail = details.find(d => 
    d.label.toLowerCase().includes('urgency') || d.label.toLowerCase().includes('level')
  );
  const severity = getSeverityColor(urgencyDetail?.value);
  
  return (
    <Card className="bg-[#1a1814]/80 border-l-4 border-l-[#d4af37] border-zinc-800 p-5 mb-4">
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-[#d4af37] flex items-center justify-center flex-shrink-0">
          <span className="text-black font-bold text-sm">{number}</span>
        </div>
        <div className="flex-1">
          <h4 className="text-white font-semibold text-lg mb-2">{title}</h4>
          
          {urgencyDetail && (
            <div className="flex items-center gap-2 mb-3">
              <span className={`w-2 h-2 rounded-full ${severity.dot}`}></span>
              <span className={`text-xs font-medium ${severity.text}`}>{severity.label}</span>
            </div>
          )}
          
          <div className="space-y-2">
            {details.filter(d => !d.label.toLowerCase().includes('urgency') && !d.label.toLowerCase().includes('level')).map((detail, i) => (
              <div key={i} className="text-sm">
                <span className="text-[#d4af37] font-medium">{detail.label}:</span>{' '}
                <span className="text-zinc-400">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

// Pattern Theme Pills
const PatternThemes = ({ themes }) => {
  const themeList = typeof themes === 'string' 
    ? themes.split(/[,;]/).map(t => t.trim()).filter(Boolean)
    : themes;
  
  const emojis = ['💡', '🎯', '⭐', '🔥', '💪', '🌟', '✨', '🚀'];
  
  return (
    <div className="flex flex-wrap gap-2 my-4">
      {themeList.map((theme, i) => (
        <span 
          key={i}
          className="px-3 py-1.5 rounded-full border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37] text-sm flex items-center gap-1.5"
        >
          <span>{emojis[i % emojis.length]}</span>
          {theme.replace(/\*\*/g, '')}
        </span>
      ))}
    </div>
  );
};

// Opportunity Tier Card
const OpportunityCard = ({ tier, title, items, description }) => {
  const tierStyles = {
    1: { border: 'border-t-[#d4af37]', emoji: '🥇', label: 'TIER 1' },
    2: { border: 'border-t-[#C0C0C0]', emoji: '🥈', label: 'TIER 2' },
    3: { border: 'border-t-[#CD7F32]', emoji: '🥉', label: 'TIER 3' }
  };
  
  const style = tierStyles[tier] || tierStyles[1];
  
  return (
    <Card className={`bg-[#1a1814]/80 border-t-4 ${style.border} border-zinc-800 p-5`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{style.emoji}</span>
        <span className="text-xs font-bold tracking-wider text-zinc-500">{style.label}</span>
      </div>
      <h4 className="text-white font-semibold mb-3">{parseInlineMarkdown(title)}</h4>
      {description && <p className="text-zinc-400 text-sm mb-3">{description}</p>}
      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
              <CheckCircle2 className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
              <span>{parseInlineMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

// Score Display Component
const ScoreDisplay = ({ score, label }) => {
  const percentage = (score / 10) * 100;
  
  return (
    <Card className="bg-[#1a1814]/80 border-[#d4af37]/30 p-6 my-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-zinc-400 text-sm font-medium uppercase tracking-wider">{label || 'Founder-Market Fit Score'}</span>
        <div className="flex items-baseline">
          <span className="text-4xl font-bold text-[#d4af37]">{score}</span>
          <span className="text-xl text-zinc-500">/10</span>
        </div>
      </div>
      <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#d4af37] to-[#FFD700] rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </Card>
  );
};

// Fit Card Component (for Best Fit / Second Best / Weakest)
const FitCard = ({ type, title, content, items }) => {
  const styles = {
    best: { border: 'border-t-[#d4af37]', icon: <Star className="w-5 h-5 text-[#d4af37]" />, label: 'BEST FIT' },
    second: { border: 'border-t-[#C0C0C0]', icon: <Medal className="w-5 h-5 text-[#C0C0C0]" />, label: 'SECOND BEST' },
    weak: { border: 'border-t-red-500/50 border-red-500/20', icon: <span className="text-xl">❌</span>, label: 'WEAKEST FIT' }
  };
  
  const style = styles[type] || styles.best;
  
  return (
    <Card className={`bg-[#1a1814]/80 border-t-4 ${style.border} border-zinc-800 p-5`}>
      <div className="flex items-center gap-2 mb-3">
        {style.icon}
        <span className="text-xs font-bold tracking-wider text-zinc-500">{style.label}</span>
      </div>
      <h4 className="text-white font-semibold mb-3">{parseInlineMarkdown(title)}</h4>
      {content && <p className="text-zinc-400 text-sm mb-3">{parseInlineMarkdown(content)}</p>}
      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
              <Zap className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
              <span>{parseInlineMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

// Recommendation Card
const RecommendationCard = ({ content, scripture }) => {
  return (
    <Card className="bg-[#252219] border-t-4 border-t-[#d4af37] border-zinc-800 p-6 my-4">
      <div className="flex items-start gap-3 mb-4">
        <Lightbulb className="w-6 h-6 text-[#d4af37] flex-shrink-0" />
        <span className="text-xs font-bold tracking-wider text-[#d4af37]">RECOMMENDATION</span>
      </div>
      <p className="text-white text-lg leading-relaxed mb-4">{parseInlineMarkdown(content)}</p>
      {scripture && (
        <p className="text-[#d4af37] italic text-sm border-t border-zinc-700 pt-4 mt-4">
          {scripture}
        </p>
      )}
    </Card>
  );
};

// Bottom Line Card
const BottomLineCard = ({ headline, action }) => {
  return (
    <Card className="bg-[#1a1814] border-zinc-700 p-6 my-4">
      <div className="flex items-center gap-2 mb-4">
        <Flame className="w-6 h-6 text-[#d4af37]" />
        <span className="text-xs font-bold tracking-wider text-zinc-400">BOTTOM LINE</span>
      </div>
      <p className="text-white font-bold text-xl mb-4">{parseInlineMarkdown(headline)}</p>
      {action && (
        <div className="border border-[#d4af37]/50 rounded-lg p-4 bg-[#d4af37]/5">
          <span className="text-xs font-bold tracking-wider text-[#d4af37] block mb-2">YOUR NEXT MOVE</span>
          <p className="text-zinc-300">{parseInlineMarkdown(action)}</p>
        </div>
      )}
    </Card>
  );
};

// Section Card Component
const SectionCard = ({ title, content, items }) => {
  return (
    <Card className="bg-[#1a1814]/80 border-l-4 border-l-[#d4af37] border-zinc-800 p-5 mb-4">
      <h3 className="text-[#d4af37] font-semibold text-lg mb-3">{title}</h3>
      {content && (
        <div className="text-zinc-300 leading-relaxed mb-3">
          {parseInlineMarkdown(content)}
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
              <CheckCircle2 className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
              <span>{parseInlineMarkdown(typeof item === 'string' ? item : item.content || '')}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

// Unfair Advantage Statement Card
const UnfairAdvantageCard = ({ content }) => {
  return (
    <Card className="bg-gradient-to-r from-[#1a1814] to-[#252219] border-[#d4af37]/50 p-6 my-4">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-6 h-6 text-[#d4af37]" />
        <span className="text-xs font-bold tracking-wider text-[#d4af37]">YOUR UNFAIR ADVANTAGE</span>
      </div>
      <p className="text-white font-semibold text-lg leading-relaxed">{parseInlineMarkdown(content)}</p>
    </Card>
  );
};

// Main AI Response Renderer Component
const AIResponseRenderer = ({ content, type = 'general' }) => {
  if (!content) return null;
  
  // Try to detect content type and render accordingly
  const renderContent = () => {
    // Check for specific patterns
    const hasTopProblems = content.includes('TOP 3') || content.includes('MOST URGENT PROBLEMS');
    const hasPatternAnalysis = content.includes('PATTERN') || content.includes('THEME');
    const hasScore = /\d+(?:\.\d+)?\s*\/\s*10/i.test(content);
    const hasUnfairAdvantage = content.includes('UNFAIR ADVANTAGE');
    const hasTiers = content.includes('TIER 1') || content.includes('TIER 2');
    const hasRecommendation = content.includes('RECOMMENDATION') || content.includes('BOTTOM LINE');
    const hasFit = content.includes('BEST FIT') || content.includes('FOUNDER-MARKET FIT');
    
    // Parse the content into sections
    const sections = [];
    const lines = content.split('\n');
    let currentSection = null;
    let problemCards = [];
    let themes = [];
    let tiers = [];
    let score = null;
    let unfairAdvantage = '';
    let recommendation = '';
    let bottomLine = { headline: '', action: '' };
    let fitCards = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Extract score
      const scoreMatch = line.match(/(\d+(?:\.\d+)?)\s*\/\s*10/);
      if (scoreMatch) {
        score = parseFloat(scoreMatch[1]);
      }
      
      // Main headers
      if (line.startsWith('##') || line.startsWith('# ')) {
        const title = line.replace(/^#+\s*\d*\.?\s*/, '').replace(/\*\*/g, '').trim();
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = { title, content: [], items: [] };
        continue;
      }
      
      // Numbered items with **#1 format
      const numMatch = line.match(/^\*\*#?(\d+)[\.\)\-\s]+(.+?)\*\*$/);
      if (numMatch && currentSection) {
        const item = {
          number: parseInt(numMatch[1]),
          title: numMatch[2].replace(/["""]/g, '').trim(),
          details: []
        };
        
        // Collect following detail lines
        for (let j = i + 1; j < lines.length; j++) {
          const detailLine = lines[j].trim();
          if (!detailLine || detailLine.startsWith('**#') || detailLine.startsWith('##')) break;
          
          const detailMatch = detailLine.match(/^-\s*\*\*(.+?)[:：]\*\*\s*(.+)$/);
          if (detailMatch) {
            item.details.push({
              label: detailMatch[1].trim(),
              value: detailMatch[2].trim()
            });
            i = j;
          } else if (detailLine.startsWith('-')) {
            item.details.push({
              label: '',
              value: detailLine.replace(/^-\s*/, '')
            });
            i = j;
          } else {
            break;
          }
        }
        
        // Check if this is a problem card
        if (currentSection.title.includes('PROBLEM') || currentSection.title.includes('URGENT')) {
          problemCards.push(item);
        } else {
          currentSection.items.push(item);
        }
        continue;
      }
      
      // Regular bullets
      if (line.startsWith('-') || line.startsWith('•')) {
        const bulletContent = line.replace(/^[-•]\s*/, '');
        if (currentSection) {
          currentSection.items.push(bulletContent);
        }
        continue;
      }
      
      // Capture themes
      if (currentSection?.title.includes('PATTERN') || currentSection?.title.includes('THEME')) {
        if (!line.startsWith('#') && !line.startsWith('*')) {
          themes.push(...line.split(/[,;]/).map(t => t.trim()).filter(Boolean));
        }
      }
      
      // Regular content
      if (currentSection) {
        currentSection.content.push(line);
      }
    }
    
    if (currentSection) {
      sections.push(currentSection);
    }
    
    // Render sections
    return (
      <div className="space-y-4">
        {sections.map((section, idx) => {
          const titleLower = section.title.toLowerCase();
          
          // Problem cards
          if (titleLower.includes('problem') || titleLower.includes('urgent')) {
            return (
              <div key={idx}>
                <h3 className="text-[#d4af37] font-semibold text-lg mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {section.title}
                </h3>
                {problemCards.length > 0 ? (
                  problemCards.map((item, i) => (
                    <ProblemCard 
                      key={i} 
                      number={item.number} 
                      title={item.title} 
                      details={item.details} 
                    />
                  ))
                ) : (
                  section.items.map((item, i) => (
                    typeof item === 'object' ? (
                      <ProblemCard 
                        key={i} 
                        number={item.number || i + 1} 
                        title={item.title || item} 
                        details={item.details || []} 
                      />
                    ) : (
                      <Card key={i} className="bg-[#1a1814]/80 border-l-4 border-l-[#d4af37] border-zinc-800 p-4 mb-3">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-[#d4af37] flex items-center justify-center flex-shrink-0">
                            <span className="text-black font-bold text-xs">{i + 1}</span>
                          </div>
                          <p className="text-zinc-300">{parseInlineMarkdown(item)}</p>
                        </div>
                      </Card>
                    )
                  ))
                )}
              </div>
            );
          }
          
          // Pattern/Theme section
          if (titleLower.includes('pattern') || titleLower.includes('theme')) {
            const themeContent = section.content.join(' ') + ' ' + section.items.join(', ');
            return (
              <div key={idx}>
                <h3 className="text-[#d4af37] font-semibold text-lg mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  {section.title}
                </h3>
                <PatternThemes themes={themeContent.split(/[,;•]/).filter(t => t.trim() && t.length < 50)} />
                {section.content.length > 0 && (
                  <p className="text-zinc-400 text-sm mt-2">{parseInlineMarkdown(section.content.join(' '))}</p>
                )}
              </div>
            );
          }
          
          // Opportunity/Ranking section
          if (titleLower.includes('opportunity') || titleLower.includes('ranking') || titleLower.includes('tier')) {
            return (
              <div key={idx}>
                <h3 className="text-[#d4af37] font-semibold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  {section.title}
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {section.items.slice(0, 3).map((item, i) => (
                    <OpportunityCard 
                      key={i}
                      tier={i + 1}
                      title={typeof item === 'string' ? item : item.title || item}
                      items={typeof item === 'object' ? item.details?.map(d => d.value) : []}
                    />
                  ))}
                </div>
              </div>
            );
          }
          
          // Unfair Advantage section
          if (titleLower.includes('unfair advantage') || titleLower.includes('advantage statement')) {
            return (
              <UnfairAdvantageCard 
                key={idx}
                content={section.content.join(' ') || section.items.join(' ')}
              />
            );
          }
          
          // Score section
          if (titleLower.includes('score') || titleLower.includes('fit score')) {
            const scoreContent = section.content.join(' ') + section.items.join(' ');
            const scoreMatch = scoreContent.match(/(\d+(?:\.\d+)?)\s*\/\s*10/);
            if (scoreMatch) {
              return (
                <ScoreDisplay 
                  key={idx}
                  score={parseFloat(scoreMatch[1])}
                  label={section.title}
                />
              );
            }
          }
          
          // Recommendation/Bottom Line
          if (titleLower.includes('recommendation') || titleLower.includes('bottom line') || titleLower.includes('next')) {
            return (
              <RecommendationCard 
                key={idx}
                content={section.content.join(' ') || section.items.join(' ')}
              />
            );
          }
          
          // Best Fit / Second Best
          if (titleLower.includes('best fit') || titleLower.includes('best match')) {
            return (
              <FitCard 
                key={idx}
                type={titleLower.includes('second') ? 'second' : titleLower.includes('weak') ? 'weak' : 'best'}
                title={section.title}
                content={section.content.join(' ')}
                items={section.items}
              />
            );
          }
          
          // Who section
          if (titleLower.includes('who') && titleLower.includes('showing up')) {
            return (
              <Card key={idx} className="bg-[#1a1814]/80 border-l-4 border-l-[#d4af37] border-zinc-800 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-[#d4af37]" />
                  <h3 className="text-[#d4af37] font-semibold">{section.title}</h3>
                </div>
                <p className="text-white leading-relaxed">{parseInlineMarkdown(section.content.join(' '))}</p>
                {section.items.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
                        {parseInlineMarkdown(typeof item === 'string' ? item : item.value || item.content || '')}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            );
          }
          
          // Default section card
          return (
            <SectionCard 
              key={idx}
              title={section.title}
              content={section.content.join(' ')}
              items={section.items}
            />
          );
        })}
        
        {/* Render score if found but not in sections */}
        {score && !sections.some(s => s.title.toLowerCase().includes('score')) && (
          <ScoreDisplay score={score} />
        )}
      </div>
    );
  };
  
  return (
    <div className="ai-response-rendered">
      {renderContent()}
    </div>
  );
};

export default AIResponseRenderer;
